import { action, asyncAction } from '@play-co/replicant';
import { CareItemID } from 'src/replicant/care/care.ruleset';
import createActions from '../config/actions/createActions';
import * as Duo from '.';
import { DuoItem } from '../care/care.ruleset';
import { Feature, sendAnalyticsEvents } from '../analytics';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { getLocale } from '../getters/user';
import * as Inventory from '../inventory';
import { getJSTDate } from '../utils/time';
import { generateReplicantChatbotPayload } from '../chatbot/messageTemplates';
import { PetSpeciesID } from '../pet';
import { isJapanese, breakJapaneseText } from '../utils/string';
import { getChatbotMessageChannel } from '../chatbot/chatbot.getters';
import { deliverChatbotMessage } from '../chatbot/chatbot.util';
import { getPetSpeciesBucket } from '../pet/pet.getters';
import { isBuruFriend } from '../friends/friends.getters';
import * as Care from '../care';
import * as Pet from '../pet';
import * as History from '../history';

export default createActions({
  sendDuoInvitation: asyncAction(
    async (
      state,
      { playerId, item }: { playerId: string; item: DuoItem },
      api,
    ) => {
      if (!state.publicProfile) {
        throw new Error('Cannot invite to duo without a public profile');
      }

      const timestamp = api.date.now();

      const { getCurrentEvolution, getPetAge, getPetName } = Pet.getters;
      const { species } = getCurrentEvolution(state)!;

      const pet = await api.asyncGetters.getPetSpecies(playerId);

      state.duo.invitations.sent.push({
        playerId,
        timestamp,
        status: 'pending',
        careItemId: item,
        pet: {
          species: pet.species!,
          name: pet.name,
          age: pet.age,
        },
        lastReminder: 0,
      });

      const payload = generateReplicantChatbotPayload({
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.INVITE,
      });

      const message = getChatbotFlexMessage({
        template: 'duoInvite',
        payload: {
          ...payload,
          alternativeWebIABBucket: pet.alternativeWebIABBucket,
        },
        data: {
          locale: getLocale(state),
          expiration: Duo.ruleset.inviteExpiration,
          item,
          inviter: {
            name: state.publicProfile!.name!,
            photo: state.publicProfile!.photo!,
            pet: species,
            petName: getPetName(state),
          },
        },
      });
      deliverChatbotMessage(state, api, {
        receiverId: playerId,
        message,
        channel: getChatbotMessageChannel(
          state,
          { name: 'duo' },
          api.date.now(),
        ),
      });

      api.postMessage.sendDuoInvitation(playerId, {
        name: state.publicProfile!.name!,
        timestamp,
        item,
        senderPet: {
          species,
          name: getPetName(state),
          age: getPetAge(state, timestamp),
        },
      });

      sendAnalyticsEvents(api, [
        {
          eventType: 'DuoInviteReceive',
          userId: playerId, // Event belongs to the player receiving the invitation.
          eventProperties: {
            friend: isBuruFriend(state, playerId),
            friendsPetId: getCurrentEvolution(state)!.species, // The friend in this case is the current player.
            item,
            sentInvitations: state.duo.invitations.received.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length, // Swap received and sent. Since state os from the inviter.
            receivedInvitations: state.duo.invitations.sent.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length, // Swap received and sent. Since state os from the inviter.
            bPointsSpent: Care.ruleset.careItems[item]?.price,
            friendID: state.id, // The friend in this case is the current player.
          },
        },
      ]);
    },
  ),

  sendDuoResponse: asyncAction(
    async (
      state,
      {
        playerId,
        timestamp,
        accepted,
        message,
      }: {
        playerId: string;
        timestamp: number;
        accepted: boolean;
        message?: string;
      },
      api,
    ) => {
      const now = api.date.now();

      const { invitations } = state.duo;

      const invitation = invitations.received.find(
        ({ timestamp: inviteTimestamp, playerId: invitePlayerId }) =>
          invitePlayerId === playerId &&
          inviteTimestamp === timestamp &&
          !Duo.utils.isExpiredInvitation(inviteTimestamp, now),
      );

      // Invitation not found or expired.
      if (!invitation) {
        return false;
      }

      invitation.status = accepted ? 'accepted' : 'declined';
      invitation.message = isJapanese(message)
        ? breakJapaneseText(message)
        : message;

      const { getters, ruleset } = Pet;
      const nextSpeciesWeights =
        ruleset.nextSpeciesWeights[getPetSpeciesBucket(state)];
      const { getCurrentEvolution, getPetName } = getters;
      const { species } = getCurrentEvolution(state)!;

      // Compute new species if accepted.
      let newSpecies = undefined;
      if (accepted) {
        newSpecies = Duo.getters.getNewSpecies(
          [
            nextSpeciesWeights[species],
            nextSpeciesWeights[invitation.pet.species! as PetSpeciesID],
          ],
          api.math.random,
        );
      }

      // Only a friend can send an invitation to someone who hasn't set up a public profile.
      // So it's safe to share private profile information.
      const name = state.publicProfile?.name ?? state.profile!.name!;

      // Should fetch the invitations before they are potentially cleared.
      const friendInvitations = accepted
        ? await api.asyncGetters.getAllDuoInvitations(playerId)
        : [];

      api.postMessage.sendDuoResponse(playerId, {
        status: accepted ? 'accepted' : 'declined',
        message,
        newSpecies,
        timestamp,
        name: state.publicProfile!.name!,
      });

      const careItemId = invitation.careItemId as CareItemID;
      const bPointsSpent = Care.ruleset.careItems[careItemId]?.price;

      sendAnalyticsEvents(api, [
        {
          eventType: 'DuoInviteResponseSend',
          userId: state.id,
          eventProperties: {
            friend: isBuruFriend(state, playerId),
            friendsPetId: invitation.pet.species,
            receivedInvitations: invitations.received.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length,
            sentInvitations: invitations.sent.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length,
            bPointsSpent,
            response: accepted ? 'accept' : 'refuse',
            friendID: playerId,
            hasMessage: !!message,
            message,
          },
        },
      ]);
      sendAnalyticsEvents(api, [
        {
          eventType: 'DuoInviteResponseReceive',
          userId: playerId, // Event belongs to the player who sent the invitation.
          eventProperties: {
            friend: isBuruFriend(state, playerId),
            friendsPetId: getCurrentEvolution(state)!.species, // The friend in this case is the current player.
            item: invitation.careItemId as DuoItem,
            sentInvitations: invitations.received.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length, // Swap sent and received. Since state is from the invitee.
            receivedInvitations: invitations.sent.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length, // Swap sent and received. Since state is from the invitee.
            bPointsSpent,
            response: accepted ? 'accept' : 'refuse',
            friendID: state.id, // The friend in this case is the current player.
            hasMessage: !!message,
            message,
          },
        },
      ]);

      // Set last response, used to show the response sequence.
      state.duo.lastResponse = {
        playerId,
        timestamp,
        accepted,
        name: state.publicProfile!.name!,
      };

      const pet = await api.asyncGetters.getPetSpecies(playerId);

      const payload = generateReplicantChatbotPayload({
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.RESPONSE,
      });

      const chatbotMessage = getChatbotFlexMessage({
        template: 'duoResponse',
        payload: {
          ...payload,
          alternativeWebIABBucket: pet.alternativeWebIABBucket,
        },
        data: {
          locale: getLocale(state),
          expiration: Duo.ruleset.inviteExpiration,
          item: invitation.careItemId,
          invitee: {
            name,
            photo: state.publicProfile?.photo ?? state.profile!.photo!,
            pet: species,
            petName: getPetName(state),
          },
        },
      });
      deliverChatbotMessage(state, api, {
        receiverId: playerId,
        message: chatbotMessage,
        channel: getChatbotMessageChannel(
          state,
          { name: 'duo' },
          api.date.now(),
        ),
      });

      // Cancelled, bail.
      if (!accepted) return true;

      // Set local debut data.
      state.duo.debut = {
        species: newSpecies!,
        rewardClaimed: false,
        careItemId: invitation.careItemId,
        inviterId: playerId,
        inviteeId: state.id,
      };

      // If response is accepted, decline all other invitations.
      const allInvitations = [
        ...invitations.received,
        ...invitations.sent,
        ...friendInvitations,
      ];
      allInvitations.forEach(
        ({ playerId: invitePlayerId, timestamp: invitationTimestamp }) => {
          // Already expired, ignore.
          if (Duo.utils.isExpiredInvitation(invitationTimestamp, now)) return;
          // Current invitation, ignore.
          if (
            (invitePlayerId === playerId || invitePlayerId === state.id) &&
            invitationTimestamp === timestamp
          ) {
            return;
          }

          const declineMessage =
            getLocale(state) === 'en'
              ? `Sorry, but I've already debuted with someone else.`
              : 'お誘いすごく嬉しいです。でも、もう他の人とデビューするって決めてしまったんです。ごめんなさい。';

          // Send message to player.
          api.postMessage.sendDuoResponse(invitePlayerId, {
            status: 'declined',
            message: declineMessage,
            timestamp: invitationTimestamp,
            name: state.publicProfile?.name ?? '',
          });
        },
      );

      // Clear all invitations, except current.
      invitations.received = [invitation];
      invitations.sent = [];

      // Kick any players currently visiting us
      const visitors = await api.asyncGetters.getCurrentVisitors(state.id);
      visitors?.forEach((visitor) => {
        api.postMessage.kickVisitor(visitor, {});
      });

      // Kick any players visiting our friend
      const friendVisitors =
        await api.asyncGetters.getCurrentVisitors(playerId);
      friendVisitors?.forEach((visitor) => {
        api.postMessage.kickVisitor(visitor, {});
      });

      return true;
    },
  ),

  claimDuoDebutReward: action((state, _, api) => {
    if (!state.duo.debut) {
      throw new Error('Cannot claim debut reward without a debut');
    }

    const { debut } = state.duo;
    if (debut.rewardClaimed) {
      throw new Error('Cannot claim debut reward, already claimed');
    }

    const { getDuoItemReward } = Duo.utils;
    const reward = getDuoItemReward(debut.careItemId as DuoItem) ?? 0;

    Inventory.modifiers.addFreeGems(
      state,
      reward,
      {
        feature: Feature.DEBUT._,
        $subFeature:
          debut.inviterId === state.id
            ? Feature.DEBUT.INVITER
            : Feature.DEBUT.INVITEE,
      },
      api,
    );

    debut.rewardClaimed = true;
  }),

  debut: action((state, _, api) => {
    const { debut } = state.duo;
    if (!debut) {
      throw new Error('Cannot debut');
    }

    if (!debut.rewardClaimed) {
      throw new Error('Cannot debut, need to claim reward first');
    }

    const friendId =
      debut.inviterId === state.id ? debut.inviteeId : debut.inviterId;

    // There will only be one invitation, either sent or received.
    // Since all other invitations are cleared after accepting.
    const invitation =
      state.duo.invitations.received[0] ?? state.duo.invitations.sent[0];

    const species = debut.species as PetSpeciesID;
    const newPet = Pet.ruleset.evolutions[getPetSpeciesBucket(state)][species];

    // Spawn new pet.
    const data = Pet.modifiers.spawnPet(
      state,
      'debut',
      {
        id: species,
        stage: newPet.stage,
        love_ini: newPet.love_ini!,
        dirtiness_ini: newPet.dirtiness_ini!,
        stomach_ini: newPet.stomach_ini!,
        tooth_acidity_ini: newPet.tooth_acidity_ini!,
        urine_ini: newPet.urine_ini!,
      },
      api.date.now(),
    );

    History.modifiers.addPlayerDuoRecord(state, {
      ...data,
      species: data.species as PetSpeciesID,
      friendID: friendId,
      timestamp: invitation.timestamp,
      friend: {
        species: invitation.pet!.species! as PetSpeciesID,
        name: invitation.pet.name ?? '',
        age: invitation.pet.age,
      },
    });

    // Track pet spawn.
    sendAnalyticsEvents(api, [
      {
        eventType: 'PetSpawn',
        userId: state.id,
        eventProperties: {
          feature: Feature.DEBUT._,
          isNew: false,
          $subFeature:
            debut.inviterId === state.id
              ? Feature.DEBUT.INVITER
              : Feature.DEBUT.INVITEE,
        },
      },
    ]);

    // Clear debut data.
    delete state.duo.debut;

    // Clear invitations.
    state.duo.invitations.received = [];
    state.duo.invitations.sent = [];
  }),

  cancelAllDuoInvitations: action((state, _, api) => {
    const { invitations } = state.duo;
    const allInvitations = [...invitations.received, ...invitations.sent];

    allInvitations.forEach(({ playerId: invitePlayerId, timestamp }) => {
      // Already expired, ignore.
      if (Duo.utils.isExpiredInvitation(timestamp, api.date.now())) return;

      // Send message to player.
      api.postMessage.sendDuoResponse(invitePlayerId, {
        status: 'cancelled',
        timestamp,
        name: state.publicProfile?.name ?? '',
      });
    });

    // Clear all invitations.
    invitations.received = [];
    invitations.sent = [];
  }),

  sendDuoReminder: asyncAction(
    async (
      state,
      { playerId, timestamp }: { playerId: string; timestamp: number },
      api,
    ) => {
      // Check if invitation is still valid.
      if (Duo.utils.isExpiredInvitation(timestamp, api.date.now())) return;

      const { getCurrentEvolution, getPetName } = Pet.getters;

      const invitation = state.duo.invitations.sent.find(
        (x) => x.playerId === playerId && x.timestamp === timestamp,
      );

      // Invitation not found.
      if (!invitation) {
        throw new Error('Duo invitation not found');
      }

      const jstDay = getJSTDate(api.date.now());
      jstDay.setHours(0, 0, 0, 0);

      const jstLastReminderDay = getJSTDate(invitation.lastReminder);
      jstLastReminderDay.setHours(0, 0, 0, 0);

      // Already reminded today.
      if (jstDay.getTime() === jstLastReminderDay.getTime()) {
        throw new Error('Cannot send duo reminder twice a day');
      }

      const now = api.date.now();
      invitation.lastReminder = now;

      const payload = generateReplicantChatbotPayload({
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.REMINDER,
      });

      const pet = await api.asyncGetters.getPetSpecies(playerId);

      const message = getChatbotFlexMessage({
        template: 'duoReminder',
        payload: {
          ...payload,
          alternativeWebIABBucket: pet.alternativeWebIABBucket,
        },
        data: {
          locale: getLocale(state),
          expiration: Math.max(
            Duo.ruleset.inviteExpiration - (now - invitation.timestamp),
            0,
          ),
          item: invitation.careItemId,
          inviter: {
            name: state.publicProfile!.name!,
            photo: state.publicProfile!.photo!,
            pet: getCurrentEvolution(state)!.species,
            petName: getPetName(state),
          },
        },
      });
      deliverChatbotMessage(state, api, {
        receiverId: playerId,
        message,
        channel: getChatbotMessageChannel(
          state,
          { name: 'duo' },
          api.date.now(),
        ),
      });

      const careItemId = invitation.careItemId as CareItemID;
      const bPointsSpent = Care.ruleset.careItems[careItemId]?.price;

      sendAnalyticsEvents(api, [
        {
          eventType: 'DuoInviteReminderReceive',
          userId: playerId, // Event belongs to the player who received the reminder.
          eventProperties: {
            friend: isBuruFriend(state, playerId),
            friendsPetId: getCurrentEvolution(state)!.species, // The friend in this case is the current player.
            item: invitation.careItemId as DuoItem,
            sentInvitations: state.duo.invitations.received.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length, // Swap received and sent. Since state is from the player who sent the reminder.
            receivedInvitations: state.duo.invitations.sent.filter(
              ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
            ).length, // Swap received and sent. Since state is from the player who sent the reminder.
            bPointsSpent,
            friendID: state.id, // The friend in this case is the current player.
          },
        },
      ]);
    },
  ),

  updateDuoLastViewed: action((state, _, api) => {
    state.duo.lastViewed = api.date.now();
  }),

  deleteLastDuoResponse: action((state, _) => {
    delete state.duo.debut;
    delete state.duo.lastResponse;
  }),

  clearExpiredDuoInvitations: action((state, _, api) => {
    const now = api.date.now() - Duo.ruleset.clearInvitesAfter;

    state.duo.invitations.sent = Duo.getters.getNonExpiredSentInvitations(
      state,
      now,
    );

    state.duo.invitations.received =
      Duo.getters.getNonExpiredReceivedInvitations(state, now);
  }),
});
